import { getSessionStorage } from "../utils/sessionStorageHelper";
import { igbSendMessage } from "../utils/postMessageHelper";
import { getServerUrl } from "../config/env";
import axios from "axios";
import ErrorHandler from "../error/errorHandler";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** API 요청 실행 */
export async function requestAPI<T>(
  url: string,
  request: any = {},
  successType: string,
  isLog: boolean = true,
  retryCount: number = 3,
  retryDelayMs: number = 5000
): Promise<T> {
  try {
    const userInfo = getSessionStorage("slotUserInfo");
    const machineId = getSessionStorage("machineId");
    const payload = {
      url,
      userId: userInfo.playerId,
      sessionId: userInfo.sessionKey,
      machineId,
      ...request,
    };

    const response: any = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });

    const result = {
      compressed: response.data.compressed,
      requestId: response.data.requestId,
      state: response.data.state,
      result: response.data.data,
    };

    if (response.data.state === "success") {
      igbSendMessage(successType, result, isLog);

      return response.data as T;
    } else if (response.data.state === "fail") {
      ErrorHandler.send(response);
      return null as T;
    } else {
      throw new Error("API request failed");
    }
  } catch (error) {
    if (retryCount > 0) {
      console.log("API Request Retry...");
      await delay(retryDelayMs);
      return requestAPI<T>(
        url,
        request,
        successType,
        isLog,
        retryCount - 1,
        retryDelayMs
      );
    } else {
      ErrorHandler.send(error);
    }
    throw error;
  }
}

/** 공용 GET 요청 */
export async function commonGetRequest(
  data: Record<string, any> = {}
): Promise<any> {
  const userInfo = getSessionStorage("slotUserInfo");
  const machineId = getSessionStorage("machineId");

  if (!userInfo || !machineId) {
    throw new Error(
      "User information or machine configuration is not available"
    );
  }

  // 쿼리스트링 생성
  const queryString = Object.entries(data)
    .filter(([_, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  const requestUrl = `${getServerUrl()}/${data.api}/${
    userInfo.playerId
  }/${machineId}${queryString ? `?${queryString}` : ""}`;

  try {
    const response = await axios.get(requestUrl);
    return response.data;
  } catch (error) {
    ErrorHandler.send(error);
    throw error;
  }
}

/** 공용 POST 요청 */
export async function commonPostRequest(
  data: Record<string, any> = {}
): Promise<any> {
  const userInfo = getSessionStorage("slotUserInfo");
  const machineId = getSessionStorage("machineId");

  if (!userInfo || !machineId) {
    throw new Error(
      "User information or machine configuration is not available"
    );
  }

  const { playerId, sessionKey } = userInfo;
  const requestUrl = `${getServerUrl()}/${data.api}`;

  try {
    const response = await axios.post(requestUrl, {
      ...data,
      playerId,
      sessionKey,
      machineId: machineId,
    });
    return response.data;
  } catch (error) {
    ErrorHandler.send(error);
    throw error;
  }
}

/**
 * 유저 정보가 필요 없는 API 요청 (GET)
 */
export async function getRequestWithoutUser(
  url: string,
  request: any,
  successType: string
): Promise<any> {
  try {
    const response = await new Promise<any>((resolve, reject) => {
      axios
        .get(url, { params: request })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });

    if (response.state === "success") {
      igbSendMessage(successType, {
        compressed: response.compressed,
        requestId: response.requestId,
        state: response.state,
        result: response.data,
      });
    } else {
      ErrorHandler.send(response);
      throw response;
    }

    return response;
  } catch (error) {
    ErrorHandler.send(error);
    throw error;
  }
}

/**
 * 유저 정보가 필요 없는 API 요청 (POST)
 */
export async function requestWithoutUser(
  url: string,
  request: any,
  successType: string
): Promise<any> {
  try {
    const response = await new Promise<any>((resolve, reject) => {
      axios
        .post(url, request, {
          headers: { "Content-Type": "application/json" },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });

    if (response.state === "success") {
      igbSendMessage(successType, {
        compressed: response.compressed,
        requestId: response.requestId,
        state: response.state,
        result: response.data.spinResponse,
      });
    } else {
      ErrorHandler.send(response);
      throw response;
    }

    return response;
  } catch (error) {
    ErrorHandler.send(error);
    throw error;
  }
}
